import {Box, createStyles, PasswordInput, Stack, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {WebhookUrlRouting} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';
import uniqueId from 'util/uniqueId';

import {BenchlingAppDefinitionGraphQL as AppDefinition} from '../../../graphql-fragments/benchling-apps/__generated/BenchlingAppDefinitionGraphQL';
import WebhookUrlRoutingRadioGroup from './WebhookUrlRoutingRadioGroup';
export type WebhookHeaderDomain = AppDefinition['webhookHeaders'][number];

export class WebhookHeader {
  key: string = '';
  value: string = '';
  submitAttempted: boolean = false;
}

const useStyles = createStyles((theme) => ({
  webhookInput: {
    width: '100%',
  },
  headersContainer: {
    width: '100%',
    paddingTop: theme.spacing.sm,
  },
  headerRow: {
    paddingBottom: theme.spacing.xs,
    display: 'flex',
  },
  headerKeyField: {
    width: '47%',
    paddingRight: theme.spacing.sm,
  },
  headerValueField: {
    width: '47%',
    paddingRight: theme.spacing.sm,
  },
}));

export interface WebhookSetupSectionProps {
  onUpdateWebhookSetup: (
    newWebhookURL: string | null,
    newWebhookHeaders: Array<{key: string; value: string}>,
    newWebhookUrlRouting?: WebhookUrlRouting
  ) => Promise<void>;
  webhookSetupIsAccessible: boolean;
  webhookUrl: string | null;
  webhookHeaders: Array<WebhookHeaderDomain> | null;
  /**
   * The current webhook URL routing value. Pass `null` when the backend has
   * no routing strategy set yet (the radio group will initialize to
   * `SUFFIXED`, the App Catalog default). Omit (or pass `undefined`) to hide
   * the routing radio group entirely — used by callers rendering legacy
   * `BenchlingApp` data, which has no routing strategy field.
   */
  webhookUrlRouting?: WebhookUrlRouting | null;
  /**
   * When true (default), the modal includes the "Custom Webhook Headers"
   * section. Pass false to hide it (e.g. when
   * `ENABLE_BENCHLING_APPS_WEBHOOK_HEADERS` is off in the new app-definitions
   * UI). Legacy callers leave this defaulted to true so they keep the
   * existing ability to set headers through the dialog.
   */
  showWebhookHeaders?: boolean;
}

export default function WebhookSetupSection({
  onUpdateWebhookSetup,
  webhookSetupIsAccessible,
  webhookUrl,
  webhookHeaders,
  webhookUrlRouting,
  showWebhookHeaders = true,
}: WebhookSetupSectionProps): JSX.Element {
  const webhookDocLink = (
    <a target="_blank" rel="noopener noreferrer" href="https://docs.benchling.com/docs/webhook-verification">
      webhook verification documentation
    </a>
  );
  const tooltip = <span>To learn more about verifying webhooks, visit our {webhookDocLink}.</span>;

  return (
    <InputWrapper
      id={uniqueId('benchlingAppDetail')}
      label="Webhook Setup"
      labelStyle={FieldLabel.STYLES.LABEL}
      help={tooltip}
      helpInteraction={FloatingInteraction.HOVER}
    >
      <Button
        text="Configure"
        buttonStyle={ButtonStyle.PRIMARY}
        disabled={!webhookSetupIsAccessible}
        size={ButtonSize.SMALL}
        onClick={(): void => {
          legacyRunModal<WebhookSetupModalProps>(WebhookSetupModal, {
            onUpdateWebhookSetup,
            initialWebhookUrl: webhookUrl,
            initialWebhookHeaders: webhookHeaders || [],
            initialWebhookUrlRouting: webhookUrlRouting,
            showWebhookHeaders,
          });
        }}
      />
    </InputWrapper>
  );
}

interface WebhookSetupModalProps {
  onUpdateWebhookSetup: (
    newWebhookURL: string | null,
    newWebhookHeaders: Array<{key: string; value: string}>,
    newWebhookUrlRouting?: WebhookUrlRouting
  ) => Promise<void>;
  initialWebhookUrl: string | null;
  initialWebhookHeaders: Array<WebhookHeaderDomain>;
  /**
   * Pass a value (or `null`) to render the routing radio group. Omit (or pass
   * `undefined`) to hide it for callers rendering legacy `BenchlingApp` data,
   * which has no routing strategy field.
   */
  initialWebhookUrlRouting?: WebhookUrlRouting | null;
  /**
   * When true (default), the modal includes the "Custom Webhook Headers"
   * section. Pass false to hide it.
   */
  showWebhookHeaders?: boolean;
  onRequestClose: () => void;
}

export function WebhookSetupModal({
  onUpdateWebhookSetup,
  initialWebhookUrl,
  initialWebhookHeaders,
  initialWebhookUrlRouting,
  showWebhookHeaders = true,
  onRequestClose,
}: WebhookSetupModalProps): JSX.Element | null {
  const {classes} = useStyles();
  // Treat the `initialWebhookUrlRouting` prop being explicitly defined (a
  // value or `null`) as the signal that the caller is rendering an
  // `AppDefinition` and can store a routing strategy. Callers rendering a
  // legacy `BenchlingApp` (e.g. `BenchlingAppPageLegacy`) omit the prop, so
  // the radio group is hidden and routing is never included in the save
  // payload.
  const showRoutingControl = initialWebhookUrlRouting !== undefined;
  const showHeadersSection = showWebhookHeaders;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(initialWebhookUrl);
  const [webhookHeaders, setWebhookHeaders] = useState(
    initialWebhookHeaders.map((h) => {
      const header = new WebhookHeader();
      header.key = h.key;
      header.value = h.value;
      return header;
    })
  );
  const [webhookUrlRouting, setWebhookUrlRouting] = useState<WebhookUrlRouting>(
    initialWebhookUrlRouting ?? WebhookUrlRouting.SUFFIXED
  );

  const submitHandler = (_: unknown): void => {
    setIsSubmitting(true);
    if (showHeadersSection) {
      const newHeaders = [...webhookHeaders];
      for (let i = 0; i < webhookHeaders.length; i++) {
        newHeaders[i].submitAttempted = true;
      }
      setWebhookHeaders(newHeaders);

      for (const c of webhookHeaders) {
        if (c.key === '' || c.value === '') {
          notifications.error('Please fill out all fields prior to saving.');
          setIsSubmitting(false);
          return;
        }
      }
    }

    const webhookHeadersPayload = webhookHeaders.map((h) => ({key: h.key, value: h.value}));
    onUpdateWebhookSetup(
      webhookUrl,
      webhookHeadersPayload,
      showRoutingControl ? webhookUrlRouting : undefined
    )
      .then(() => {
        onRequestClose();
        notifications.success(<>Successfully updated app</>);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const MODAL_FOOTER: Array<ModalButton> = [
    {text: 'Cancel', onClick: onRequestClose, cancel: true, disabled: isSubmitting},
    {
      text: isSubmitting ? 'Saving...' : 'Save',
      onClick: submitHandler,
      submit: true,
      style: 'primary',
      disabled: isSubmitting,
    },
  ];

  const webhookDocLink = (
    <a target="_blank" rel="noopener noreferrer" href="https://docs.benchling.com/docs/webhook-verification">
      webhook verification documentation
    </a>
  );
  const tooltip = <span>To learn more about verifying webhooks, visit our {webhookDocLink}.</span>;

  return (
    <Modal onRequestClose={onRequestClose} buttons={MODAL_FOOTER} title="Webhook Setup">
      <Stack spacing="md">
        <InputWrapper
          id={uniqueId('webhookSetupModal')}
          label="Webhook URL"
          labelStyle={FieldLabel.STYLES.LABEL}
          help={tooltip}
          helpInteraction={FloatingInteraction.HOVER}
        >
          <Box className={classes.webhookInput}>
            <EditableTextField
              emptyValueAllowed={true}
              requestChange={(newWebhookUrl: string) => {
                setWebhookUrl(newWebhookUrl);
              }}
              type={TextFieldType.TEXT}
              value={webhookUrl || ''}
              isFullWidth={true}
              dataTestAttrs={{component: 'WebhookSetupModal', element: 'webhookUrlField'}}
            />
          </Box>
        </InputWrapper>
        {showRoutingControl && (
          <WebhookUrlRoutingRadioGroup
            webhookUrlRouting={webhookUrlRouting}
            onUpdateWebhookUrlRouting={setWebhookUrlRouting}
          />
        )}
        {showHeadersSection && (
          <Box>
            <InputWrapper
              id={uniqueId('webhookSetupModal')}
              label="Custom Webhook Headers"
              labelStyle={FieldLabel.STYLES.LABEL}
            >
              <Box className={classes.headersContainer}>
                {webhookHeaders.map((header: WebhookHeader, i) => (
                  <WebhookHeaderRow
                    key={i}
                    header={header}
                    i={i}
                    webhookHeaders={webhookHeaders}
                    setWebhookHeaders={setWebhookHeaders}
                  />
                ))}
              </Box>
            </InputWrapper>
            <Button
              text="+ Add Header"
              buttonStyle={ButtonStyle.TEXT}
              disabled={false}
              size={ButtonSize.SMALL}
              onClick={(): void => {
                setWebhookHeaders([...webhookHeaders, new WebhookHeader()]);
              }}
            />
          </Box>
        )}
      </Stack>
    </Modal>
  );
}

interface WebhookHeaderRowProps {
  header: WebhookHeader;
  i: number;
  webhookHeaders: Array<WebhookHeader>;
  setWebhookHeaders: React.Dispatch<React.SetStateAction<Array<WebhookHeader>>>;
}

export function WebhookHeaderRow({
  header,
  i,
  webhookHeaders,
  setWebhookHeaders,
}: WebhookHeaderRowProps): JSX.Element | null {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  return (
    <Box className={classes.headerRow}>
      <Box className={classes.headerKeyField}>
        <TextInput
          size="md"
          value={header.key}
          aria-label="Header key"
          error={header.submitAttempted && header.key === '' ? 'Field cannot be blank' : null}
          onChange={(e: InputChangeEvent) => {
            const newHeaders = [...webhookHeaders];
            newHeaders[i].key = e.target.value;
            setWebhookHeaders(newHeaders);
          }}
          placeholder="Header key"
          styles={{error: {display: 'none'}}}
        />
      </Box>
      <Box className={classes.headerValueField}>
        <PasswordInput
          size="md"
          value={header.value}
          aria-label="Header value"
          error={header.submitAttempted && header.value === '' ? 'Field cannot be blank' : null}
          onChange={(e: InputChangeEvent) => {
            const newHeaders = [...webhookHeaders];
            newHeaders[i].value = e.target.value;
            setWebhookHeaders(newHeaders);
          }}
          placeholder="Header value"
        />
      </Box>
      <Button
        buttonStyle={ButtonStyle.CIRCLE_ICON}
        size={ButtonSize.SMALL}
        icon="delete"
        onClick={() => {
          const newHeaders = webhookHeaders.slice(0, i).concat(webhookHeaders.slice(i + 1));
          setWebhookHeaders(newHeaders);
        }}
      />
    </Box>
  );
}
